import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../auth/authStore.js';
import {
  applySessionResultToCaches,
  invalidateProgress,
  type SessionCompletionResult,
} from '../progress/hooks.js';
import { usePendingExamTaskStore, type PendingExamTask } from './pendingExamTaskStore.js';
import { usePendingExamResultStore } from './pendingExamResultStore.js';
import { useExamStore } from './examStore.js';
import type { ExamResult } from './attempt.js';
import {
  EXAM_RESULT_COLUMNS,
  resultToPayload,
  rowToResult,
  type ExamResultPayload,
} from './results.js';

/**
 * ტესტი progress on the server: every finished task (all items answered)
 * goes to `complete_exam_task` — XP (+1 per right answer, 30/day), the
 * streak, and the "finish N test tasks" missions. Same shape as the
 * flashcard sessions: queue-first, idempotent per (attempt, task),
 * caches written from the result, missions refetched.
 */

export interface ExamTaskPayload {
  attemptId: string;
  /** Test number, 1–120. */
  test: number;
  /** Task number, 1–6. */
  task: number;
  correct: number;
}

/** Direct RPC used by the reporter and the flusher. Idempotent per (attempt, task). */
export async function completeExamTaskDirect(p: ExamTaskPayload): Promise<SessionCompletionResult> {
  const { data, error } = await supabase.rpc('complete_exam_task', {
    p_attempt_id: p.attemptId,
    p_test: p.test,
    p_task: p.task,
    p_correct: p.correct,
  });
  if (error) throw error;
  return data as unknown as SessionCompletionResult;
}

export const pendingIdOf = (attemptId: string, task: number) => `${attemptId}:${task}`;

/**
 * Returns a function that reports finished tasks. Each is enqueued first,
 * then sent; on failure it simply stays queued for the ExamFlusher.
 */
export function useReportExamTasks() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const enqueue = usePendingExamTaskStore((s) => s.enqueue);
  const remove = usePendingExamTaskStore((s) => s.remove);

  return useCallback(
    (
      attemptId: string,
      test: number,
      tasks: { task: number; correct: number }[],
      onResult?: (result: SessionCompletionResult) => void,
    ) => {
      if (!user || tasks.length === 0) return;
      const now = Date.now();
      const entries: PendingExamTask[] = tasks.map(({ task, correct }) => ({
        id: pendingIdOf(attemptId, task),
        attempt_id: attemptId,
        test,
        task,
        correct,
        userId: user.id,
        queuedAt: now,
      }));
      enqueue('tasks', entries);
      for (const e of entries) {
        completeExamTaskDirect({ attemptId, test, task: e.task, correct: e.correct })
          .then((result) => {
            remove('tasks', [e.id]);
            applySessionResultToCaches(qc, user.id, result);
            invalidateProgress(qc, user.id);
            onResult?.(result);
          })
          .catch(() => {
            /* stays queued; the ExamFlusher retries with backoff */
          });
      }
    },
    [user, enqueue, remove, qc],
  );
}

/* ------------------------- finished sittings ------------------------- */

export const EXAM_RESULTS_KEY = (userId: string) => ['exam', 'results', userId] as const;

/** Direct RPC used by the saver and the flusher. Idempotent per attempt. */
export async function saveExamResultDirect(p: ExamResultPayload): Promise<void> {
  const { error } = await supabase.rpc('save_exam_result', p);
  if (error) throw error;
}

/**
 * Returns a function that sends a finished sitting to `exam_results`.
 * Enqueued first, then sent; on failure it stays queued for the ExamFlusher.
 */
export function useSaveExamResult() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const enqueue = usePendingExamResultStore((s) => s.enqueue);
  const remove = usePendingExamResultStore((s) => s.remove);

  return useCallback(
    (result: ExamResult) => {
      const payload = resultToPayload(result);
      if (!user || !payload) return;
      const id = payload.p_attempt_id;
      enqueue('results', [{ id, payload, userId: user.id, queuedAt: Date.now() }]);
      saveExamResultDirect(payload)
        .then(() => {
          remove('results', [id]);
          void qc.invalidateQueries({ queryKey: EXAM_RESULTS_KEY(user.id) });
        })
        .catch(() => {
          /* stays queued; the ExamFlusher retries with backoff */
        });
    },
    [user, enqueue, remove, qc],
  );
}

/**
 * Fetch this account's finished sittings and merge them into the device's
 * best/last, so the test list and results follow the account. Off for the
 * demo routes (no account). Reading is RLS-scoped to the user's own rows.
 */
export function useExamResultsSync(enabled: boolean) {
  const { user } = useAuth();
  const merge = useExamStore((s) => s.mergeRemoteResults);
  const on = enabled && !!user;
  const { data } = useQuery({
    queryKey: user ? EXAM_RESULTS_KEY(user.id) : ['exam', 'results', 'anon'],
    enabled: on,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('exam_results')
        .select(EXAM_RESULT_COLUMNS)
        .order('finished_at', { ascending: false })
        .limit(2000);
      if (error) throw error;
      return rows.map(rowToResult);
    },
  });
  useEffect(() => {
    if (on && user && data) merge(user.id, data);
  }, [on, user, data, merge]);
}
